import { useState } from "react";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  FileText,
  Info,
  Lock,
  RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FichaColaborador } from "@/components/FichaColaborador";
import { trpc } from "@/lib/trpc";
import { formatBRL, formatarData, diaDaSemana, nomeMes } from "@/lib/utils";

// ============================================================
// R4 — Meu extrato
// ============================================================
// A tela do colaborador. Mostra SÓ os dados dele: o backend resolve o
// profissional pelo usuário logado e não aceita id de ninguém, então não há
// como esta página pedir o extrato de outra pessoa nem por engano.
//
// O botão "Confirmo meus turnos deste mês" é o outro lado da regra do Bryan
// (04/09): o fechamento é conferido COM a pessoa. Ele carimba a data do
// aceite dela — e não fecha nada. Quem fecha a competência continua sendo o
// financeiro, em Conferência; quem paga continua sendo a clínica.
//
// O que a pessoa vê de dinheiro vem inteiro da clínica (fechamento, pagamentos
// e recibos). Se o canal estiver fora do ar, a tela diz isso em vez de
// estimar: número inventado de salário é pior do que número ausente.

const ESTILO_TURNO: Record<string, string> = {
  realizado: "bg-emerald-100 text-emerald-800",
  cancelado: "bg-slate-200 text-slate-600",
  falta: "bg-red-100 text-red-700",
  agendado: "bg-amber-100 text-amber-800",
  pendente: "bg-amber-100 text-amber-800",
  aprovado: "bg-sky-100 text-sky-800",
};

const ESTILO_FECHAMENTO: Record<string, string> = {
  aberto: "bg-amber-100 text-amber-800",
  parcial: "bg-sky-100 text-sky-800",
  pago: "bg-emerald-100 text-emerald-800",
  cancelado: "bg-slate-200 text-slate-600",
};

function reais(centavos: number | null | undefined): string {
  return formatBRL((centavos ?? 0) / 100);
}

function dataHora(valor: Date | string | null | undefined): string {
  if (!valor) return "";
  const d = valor instanceof Date ? valor : new Date(valor);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

/**
 * Abre o PDF que a clínica devolveu em base64. Tenta aba nova; se o navegador
 * bloquear (o clique já passou por um await), cai no download. O nome do
 * arquivo vem do servidor — é ele a fonte única desse nome.
 */
function abrirPdfBase64(base64: string, nomeArquivo: string): void {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  const aba = window.open(url, "_blank");
  if (!aba) {
    const a = document.createElement("a");
    a.href = url;
    a.download = nomeArquivo;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function MeuExtrato() {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getUTCFullYear());
  const [mes, setMes] = useState(hoje.getUTCMonth() + 1);

  const [fichaAberta, setFichaAberta] = useState(false);

  const utils = trpc.useUtils();
  const q = trpc.meuExtrato.resumo.useQuery({ ano, mes });

  // A PRÓPRIA ficha, em leitura. `colaboradores.minha` resolve o profissional
  // pelo login — a tela não manda id de ninguém — e a permissão do outro lado
  // devolve podeEditar=false para quem não é do financeiro. Serve para o
  // veterinário ver o próprio cadastro fiscal e o que falta nele sem precisar
  // de /colaboradores, que é a tela do RH e continua fechada para ele.
  const ficha = trpc.colaboradores.minha.useQuery(undefined, { retry: false });
  const minhaFicha = ficha.data && !ficha.data.semProfissional ? ficha.data : null;
  const meuProfissionalId = minhaFicha?.profissional?.id ?? null;
  const checklist = minhaFicha?.dados?.checklist ?? null;
  const pendencias = checklist?.totalPendencias ?? 0;

  const confirmar = trpc.meuExtrato.confirmar.useMutation({
    onSuccess: (r) => {
      utils.meuExtrato.resumo.invalidate();
      alert(
        r.jaEstava
          ? "Você já tinha confirmado este mês. A data do primeiro aceite continua valendo."
          : "Confirmação registrada. O financeiro vê o seu aceite ao fechar a competência.",
      );
    },
    onError: (e) => alert(e.message),
  });

  const recibo = trpc.meuExtrato.recibo.useMutation({
    onSuccess: (r) => {
      abrirPdfBase64(r.base64, r.nomeArquivo);
      if (!r.numero) {
        alert(
          "O recibo saiu como RASCUNHO, sem número, e não vale como documento. " +
            (r.faltando.join(" | ") ||
              "Falta completar o seu cadastro na clínica (CPF/CNPJ e endereço). Fale com o financeiro."),
        );
      }
    },
    onError: (e) => alert(e.message),
  });

  function navegar(delta: number) {
    let novo = mes + delta;
    let novoAno = ano;
    if (novo < 1) {
      novo = 12;
      novoAno--;
    }
    if (novo > 12) {
      novo = 1;
      novoAno++;
    }
    setMes(novo);
    setAno(novoAno);
  }

  const d = q.data;
  const ocupado = confirmar.isPending || recibo.isPending || q.isFetching;
  const jaConfirmou = Boolean(d?.conferencia?.confirmadoPeloProfissionalEm);
  const fechamento = d?.clinica.fechamento ?? null;
  const pagamentos = (d?.clinica.pagamentos ?? []).filter((p) => !p.estornadoEm);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Meu extrato</h1>
          <p className="text-sm text-slate-600">
            {d?.profissional?.nome
              ? `${d.profissional.nome} — o que você tem a receber no mês.`
              : "O que você tem a receber no mês: turnos, o que foi apurado e o que já foi pago."}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => q.refetch()} disabled={q.isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${q.isFetching ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      <div className="flex items-center justify-center gap-2">
        <Button variant="outline" size="icon" onClick={() => navegar(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-44 text-center text-lg font-semibold text-slate-900">
          {nomeMes(mes)} {ano}
        </div>
        <Button variant="outline" size="icon" onClick={() => navegar(1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {q.isLoading && <div className="py-10 text-center text-slate-500">Carregando seu mês…</div>}

      {q.error && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-red-700">Não deu para carregar o seu extrato</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-700">
            <p>{q.error.message}</p>
            <Button variant="outline" onClick={() => q.refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" /> Tentar de novo
            </Button>
          </CardContent>
        </Card>
      )}

      {d && !d.temFicha && (
        <Card>
          <CardContent className="space-y-2 py-10 text-center">
            <p className="text-sm font-medium text-slate-700">
              O seu login ainda não está ligado a uma ficha de profissional.
            </p>
            <p className="mx-auto max-w-md text-xs text-slate-500">
              Sem esse vínculo o sistema não sabe quais turnos são seus. Peça ao administrador para
              ligar o seu usuário à sua ficha em Profissionais — leva um minuto.
            </p>
          </CardContent>
        </Card>
      )}

      {d && d.temFicha && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat label="Turnos no mês" value={`${d.totais.realizados}/${d.totais.qtd}`} />
            <Stat label="Previsto" value={reais(d.totais.previstoCentavos)} />
            <Stat
              label="Apurado (realizados)"
              value={reais(
                d.conferencia?.status === "conferido"
                  ? d.conferencia.totalApuradoCentavos
                  : d.totais.apuradoCentavos,
              )}
              destaque
            />
            <Stat
              label="Já pago"
              value={fechamento ? reais(fechamento.totalPagoCentavos) : "—"}
            />
          </div>

          {/* ── Conferência do mês + o aceite do colaborador ─────────────── */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Conferência de {nomeMes(mes)}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex flex-wrap items-center gap-2 text-slate-700">
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${
                    d.conferencia?.status === "conferido"
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-slate-200 text-slate-600"
                  }`}
                >
                  {d.conferencia?.status === "conferido"
                    ? "fechada pelo financeiro"
                    : (d.conferencia?.status ?? "ainda não aberta")}
                </span>
                {d.conferencia?.conferidoEm && (
                  <span className="text-xs text-slate-500">
                    em {dataHora(d.conferencia.conferidoEm)}
                  </span>
                )}
                {d.totais.pendentes > 0 && (
                  <span className="text-xs text-amber-700">
                    {d.totais.pendentes} turno(s) ainda sem marcação de realizado/falta
                  </span>
                )}
              </div>

              {jaConfirmou ? (
                <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                  <Check className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Você confirmou os turnos deste mês em{" "}
                    {dataHora(d.conferencia?.confirmadoPeloProfissionalEm)}. Se algo estiver errado,
                    fale com o financeiro — a correção é feita na Conferência, não por aqui.
                  </span>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-slate-600">
                    Confira a lista abaixo. O seu "confirmo" fica registrado com data e hora ao lado
                    do fechamento do financeiro — ele <strong>não</strong> fecha a competência nem
                    autoriza pagamento; é o registro de que você viu e concordou.
                  </p>
                  <Button
                    disabled={ocupado || d.totais.qtd === 0}
                    onClick={() => {
                      if (
                        !confirm(
                          `Confirmar que os turnos de ${nomeMes(mes)}/${ano} listados aqui estão certos?`,
                        )
                      )
                        return;
                      confirmar.mutate({ ano, mes });
                    }}
                  >
                    <Check className="mr-2 h-4 w-4" />
                    Confirmo meus turnos deste mês
                  </Button>
                  {d.totais.qtd === 0 && (
                    <p className="text-xs text-slate-500">
                      Você não tem turno neste mês — não há o que confirmar.
                    </p>
                  )}
                </div>
              )}

              {d.conferencia?.reabertoEm && (
                <p className="text-xs text-amber-700">
                  Esta competência foi reaberta em {dataHora(d.conferencia.reabertoEm)}
                  {d.conferencia.reabertoMotivo ? `: ${d.conferencia.reabertoMotivo}` : "."}
                </p>
              )}
            </CardContent>
          </Card>

          {/* ── Turnos do mês ────────────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Meus turnos ({d.turnos.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {d.turnos.length === 0 ? (
                <p className="px-6 py-8 text-center text-sm text-slate-400">
                  Nenhum turno neste mês.
                </p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {d.turnos.map((t) => (
                    <li
                      key={t.turnoId}
                      className="flex flex-wrap items-center justify-between gap-2 px-6 py-3 text-sm"
                    >
                      <span className="flex flex-wrap items-center gap-2 text-slate-700">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: t.tipoTurnoCor }}
                        />
                        <span className="font-medium text-slate-900">
                          {formatarData(t.data)} · {diaDaSemana(t.data)}
                        </span>
                        <span>
                          {t.tipoTurnoNome} · {t.horaInicio}–{t.horaFim}
                        </span>
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-medium ${
                            ESTILO_TURNO[t.status] ?? "bg-slate-200 text-slate-600"
                          }`}
                        >
                          {t.status}
                        </span>
                        {t.acionado && <Badge variant="outline">acionado</Badge>}
                        {t.observacaoConferencia && (
                          <span className="text-xs text-slate-500">
                            {t.observacaoConferencia}
                          </span>
                        )}
                      </span>
                      <span className="text-right">
                        <span
                          className={
                            t.status === "realizado"
                              ? "font-medium text-slate-900"
                              : "text-slate-400 line-through"
                          }
                        >
                          {reais(
                            t.status === "realizado"
                              ? t.valorApuradoCentavos
                              : t.valorPrevistoCentavos,
                          )}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* ── O que a clínica registrou: fechamento, pagamentos, recibos ── */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Pagamento deste mês</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {d.clinica.semVinculo && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Sua ficha ainda não está ligada ao cadastro da clínica, que é onde a conta a
                    pagar e o recibo nascem. Fale com o financeiro.
                  </span>
                </div>
              )}

              {!d.clinica.semVinculo && !d.clinica.canal && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Canal com a clínica não configurado neste ambiente.</span>
                </div>
              )}

              {d.clinica.erro && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    A clínica não respondeu agora, então o que já foi pago não aparece aqui. Nada
                    foi estimado. ({d.clinica.erro})
                  </span>
                </div>
              )}

              {d.clinica.liberado === false && (
                <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-600">
                  <Lock className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                  <span>
                    Os pagamentos pelo sistema ainda não foram liberados na clínica. Repasses feitos
                    por fora continuam sendo lançados à mão pelo financeiro e vão aparecer aqui
                    quando forem registrados.
                  </span>
                </div>
              )}

              {!fechamento && !d.clinica.erro && !d.clinica.semVinculo && (
                <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-600">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                  <span>
                    Este mês ainda não virou conta a pagar na clínica. Isso acontece depois que o
                    financeiro fecha a competência.
                  </span>
                </div>
              )}

              {fechamento && (
                <>
                  <div className="flex flex-wrap items-center gap-2 text-slate-600">
                    <span className="font-medium text-slate-900">
                      Fechamento #{fechamento.id}
                    </span>
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-medium ${
                        ESTILO_FECHAMENTO[fechamento.status] ?? "bg-slate-200 text-slate-600"
                      }`}
                    >
                      {fechamento.status}
                    </span>
                    <span className="text-slate-400">·</span>
                    <span>{fechamento.qtdTurnos} turno(s)</span>
                    {fechamento.empresa && (
                      <>
                        <span className="text-slate-400">·</span>
                        <span>{fechamento.empresa}</span>
                      </>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <Numero rotulo="Apurado" valor={reais(fechamento.totalApuradoCentavos)} />
                    <Numero
                      rotulo="Pago"
                      valor={reais(fechamento.totalPagoCentavos)}
                      tom="text-emerald-700"
                    />
                    <Numero
                      rotulo="A receber"
                      valor={reais(fechamento.saldoCentavos)}
                      tom={fechamento.saldoCentavos > 0 ? "text-slate-900" : "text-slate-400"}
                    />
                  </div>

                  <div>
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Pagamentos recebidos ({pagamentos.length})
                    </div>
                    {pagamentos.length === 0 ? (
                      <p className="py-2 text-slate-400">Nenhum pagamento registrado ainda.</p>
                    ) : (
                      <ul className="divide-y divide-slate-100">
                        {pagamentos.map((p) => (
                          <li
                            key={p.id}
                            className="flex flex-wrap items-center justify-between gap-2 py-2"
                          >
                            <span className="text-slate-700">
                              {formatarData(p.data)} · {p.forma}
                              {p.referenciaExterna ? ` · ref. ${p.referenciaExterna}` : ""}
                            </span>
                            <span className="flex items-center gap-3">
                              <span className="font-medium text-slate-900">
                                {reais(p.valorCentavos)}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={ocupado}
                                title="Baixa o recibo deste pagamento, emitido pela clínica."
                                onClick={() => recibo.mutate({ ano, mes, pagamentoId: p.id })}
                              >
                                <FileText className="mr-1 h-3.5 w-3.5" />
                                Recibo
                              </Button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ── A minha ficha, em leitura ──────────────────────────────────────
          Fica fora do bloco do mês de propósito: o cadastro não muda de
          competência para competência, e o que falta nele trava recibo e
          contrato o ano inteiro. Nada aqui é editável — quem corrige cadastro
          fiscal é o financeiro, em Colaboradores. */}
      {meuProfissionalId !== null && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">Minha ficha</CardTitle>
              <Button variant="outline" size="sm" onClick={() => setFichaAberta((s) => !s)}>
                {fichaAberta ? "Fechar ficha" : "Ver minha ficha"}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {pendencias > 0 ? (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Faltam <strong>{pendencias}</strong> dado(s) no seu cadastro:{" "}
                  {checklist?.resumo ?? "abra a ficha para ver a lista."} Enquanto isso o recibo sai
                  como rascunho, sem número. Leve a lista ao financeiro — quem grava esses campos é
                  ele, na clínica.
                </span>
              </div>
            ) : checklist ? (
              <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-800">
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Seu cadastro está completo para a modalidade atual.</span>
              </div>
            ) : minhaFicha?.erro ? (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{minhaFicha.erro}</span>
              </div>
            ) : (
              <p className="text-slate-500">Carregando o seu cadastro…</p>
            )}

            <p className="text-xs text-slate-500">
              Seus dados cadastrais e documentos moram no sistema da clínica. Aqui você só lê: se
              algo estiver errado, fale com o financeiro.
            </p>

            {fichaAberta && (
              <div className="border-t border-slate-100 pt-3">
                <FichaColaborador profissionalId={meuProfissionalId} somenteLeitura />
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, destaque }: { label: string; value: string; destaque?: boolean }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
        <div className={`mt-1 text-2xl font-bold ${destaque ? "text-brand-600" : "text-slate-900"}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function Numero({ rotulo, valor, tom }: { rotulo: string; valor: string; tom?: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-xs text-slate-500">{rotulo}</div>
      <div className={`text-lg font-semibold ${tom ?? "text-slate-900"}`}>{valor}</div>
    </div>
  );
}
